import React, { useState, useEffect, useContext } from "react";
import { useFirestore } from "../firebase/FirestoreContext";
import { collection, getDocs, addDoc, updateDoc, doc, deleteDoc } from "firebase/firestore";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EnvContext } from "../../context/EnvContext";
import config from "../../config";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage"; // Firebase Storage imports
// image MUI icon
import CircularProgress from "@mui/material/CircularProgress";
import { ImageOutlined } from "@mui/icons-material";

const ProductVibes = () => {
  const environment = useContext(EnvContext);
  const db = useFirestore();
  const storage = getStorage();

  const collectionPath = environment === "staging" ? config.enveironment.staging.collectionPath : config.enveironment.production.collectionPath;

  const [uploading, setUploading] = useState({});
  const [vibes, setVibes] = useState([]);
  const [newVibeName, setNewVibeName] = useState("");
  const [editMode, setEditMode] = useState(null);
  const [editedVibeName, setEditedVibeName] = useState("");
  const [tagInput, setTagInput] = useState({});
  const [imageInput, setImageInput] = useState({});
  const [newVibeCategoryName, setNewVibeCategoryName] = useState("");

  useEffect(() => {
    fetchVibes();
  }, [db, collectionPath]);

  const fetchVibes = async () => {
    const vibesCollectionRef = collection(db, `${collectionPath}SooperstockSettings/ProductVibes/DataBase`);
    const querySnapshot = await getDocs(vibesCollectionRef);
    const vibesArray = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setVibes(vibesArray);
  };

  const handleAddVibe = async () => {
    if (!newVibeName.trim()) {
      toast.error("Vibe name cannot be empty");
      return;
    }
    if (!newVibeCategoryName.trim()) {
      toast.error("Category key cannot be empty");
      return;
    }

    try {
      const newVibe = {
        name: newVibeName,
        categoryName: newVibeCategoryName,
        tags: [],
        homepage_image_url: "",
        shop_image_url: "",
      };
      const docRef = await addDoc(collection(db, `${collectionPath}SooperstockSettings/ProductVibes/DataBase`), newVibe);
      toast.success("Vibe added successfully");
      setNewVibeName("");
      setNewVibeCategoryName("");
      fetchVibes();
    } catch (error) {
      toast.error("Error adding vibe");
    }
  };

  const handleEditVibe = async (vibeId) => {
    if (!editedVibeName.trim()) {
      toast.error("Vibe name cannot be empty");
      return;
    }

    try {
      const vibeRef = doc(db, `${collectionPath}SooperstockSettings/ProductVibes/DataBase`, vibeId);
      await updateDoc(vibeRef, { name: editedVibeName, categoryName: newVibeCategoryName });
      toast.success("Vibe updated successfully");
      setEditMode(null);
      fetchVibes();
    } catch (error) {
      toast.error("Error updating vibe");
    }
  };

  const handleDeleteVibe = async (vibeId) => {
    if (!window.confirm("Are you sure you want to delete this vibe?")) return;

    try {
      const vibeRef = doc(db, `${collectionPath}SooperstockSettings/ProductVibes/DataBase`, vibeId);
      await deleteDoc(vibeRef);
      toast.success("Vibe deleted successfully");
      fetchVibes();
    } catch (error) {
      toast.error("Error deleting vibe");
    }
  };

  const handleUploadImage = async (vibeId, file, imageType) => {
    if (!file) return;
    try {
      // Mark this vibe as uploading
      setUploading((prev) => ({ ...prev, [`${vibeId}-${imageType}`]: true }));
      const storageRef = ref(storage, `Vibes/${vibeId}-${imageType}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      // Update Firestore with the image URL for the specific image type
      const vibeRef = doc(db, `${collectionPath}SooperstockSettings/ProductVibes/DataBase`, vibeId);

      // Use different field based on image type
      const updateField = imageType === "homepage" ? "homepage_image_url" : "shop_image_url";
      await updateDoc(vibeRef, { [updateField]: url });

      // Update local state with new image URL
      setVibes((prev) => prev.map((vibe) => (vibe.id === vibeId ? { ...vibe, [updateField]: url } : vibe)));

      toast.success(`${imageType === "homepage" ? "Homepage" : "Shop"} image uploaded successfully`);
    } catch (error) {
      toast.error("Error uploading image");
    } finally {
      // Remove loading flag
      setUploading((prev) => ({ ...prev, [`${vibeId}-${imageType}`]: false }));
    }
  };

  return (
    <div>
      <ToastContainer position="top-center" autoClose={5000} hideProgressBar={false} />
      <h2>Product Vibes</h2>
      <table style={styles.table}>
        <thead>
          <tr style={{ backgroundColor: "rgba(49,57,66,.1)" }}>
            <th style={styles.th}>Vibes</th>
            <th style={styles.th}>Category</th>
            <th style={styles.th}>Homepage Image</th>
            <th style={styles.th}>Shop Image</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {vibes.map((vibe, index) => (
            <tr
              key={vibe.id}
              style={{
                backgroundColor: index % 2 === 0 ? "#fafafa" : "",
                borderBottom: "1px solid #ccc",
              }}
            >
              {/* Vibe Name */}
              <td>
                {editMode === vibe.id ? (
                  <input type="text" value={editedVibeName} onChange={(e) => setEditedVibeName(e.target.value)} style={styles.input} />
                ) : (
                  <span>{vibe.name}</span>
                )}
              </td>

              {/* Category Name */}
              <td>
                {editMode === vibe.id ? (
                  <input
                    type="text"
                    value={newVibeCategoryName}
                    onChange={(e) => setNewVibeCategoryName(e.target.value)}
                    placeholder="Category Key (e.g., sleep)"
                    style={styles.input}
                  />
                ) : (
                  <span style={{ color: "gray" }}>{vibe.categoryName || ""}</span>
                )}
              </td>

              {/* Homepage Image */}
              <td>
                <div style={{ display: "flex", alignItems: "center" }}>
                  {/* Homepage Image Preview */}
                  <div style={{ position: "relative", marginRight: "10px" }}>
                    {vibe.homepage_image_url ? (
                      <img
                        src={vibe.homepage_image_url}
                        alt={`${vibe.name} homepage`}
                        style={{
                          width: "50px",
                          height: "50px",
                          objectFit: "cover",
                          borderRadius: "5px",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "50px",
                          height: "50px",
                          borderRadius: "5px",
                          backgroundColor: "#ccc",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          color: "gray",
                        }}
                      >
                        <ImageOutlined />
                      </div>
                    )}
                    {uploading[`${vibe.id}-homepage`] && (
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "50px",
                          height: "50px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          backgroundColor: "rgba(255,255,255,0.7)",
                        }}
                      >
                        <CircularProgress size={24} />
                      </div>
                    )}
                  </div>

                  <input
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleUploadImage(vibe.id, file, "homepage");
                      }
                    }}
                    accept="image/*"
                    style={{ display: "none" }}
                    id={`homepage-image-upload-${vibe.id}`}
                  />

                  <label
                    htmlFor={`homepage-image-upload-${vibe.id}`}
                    style={{
                      cursor: "pointer",
                      padding: "8px 10px",
                      backgroundColor: "lightblue",
                      color: "black",
                      borderRadius: "4px",
                      fontSize: ".7rem",
                    }}
                  >
                    Upload Image
                  </label>
                </div>
              </td>

              {/* Shop Image */}
              <td>
                <div style={{ display: "flex", alignItems: "center" }}>
                  {/* Shop Image Preview */}
                  <div style={{ position: "relative", marginRight: "10px" }}>
                    {vibe.shop_image_url ? (
                      <img
                        src={vibe.shop_image_url}
                        alt={`${vibe.name} shop`}
                        style={{
                          width: "50px",
                          height: "50px",
                          objectFit: "cover",
                          borderRadius: "5px",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "50px",
                          height: "50px",
                          borderRadius: "5px",
                          backgroundColor: "#ccc",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          color: "gray",
                        }}
                      >
                        <ImageOutlined />
                      </div>
                    )}
                    {uploading[`${vibe.id}-shop`] && (
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "50px",
                          height: "50px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          backgroundColor: "rgba(255,255,255,0.7)",
                        }}
                      >
                        <CircularProgress size={24} />
                      </div>
                    )}
                  </div>

                  <input
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleUploadImage(vibe.id, file, "shop");
                      }
                    }}
                    accept="image/*"
                    style={{ display: "none" }}
                    id={`shop-image-upload-${vibe.id}`}
                  />

                  <label
                    htmlFor={`shop-image-upload-${vibe.id}`}
                    style={{
                      cursor: "pointer",
                      padding: "8px 10px",
                      backgroundColor: "lightblue",
                      color: "black",
                      borderRadius: "4px",
                      fontSize: ".7rem",
                    }}
                  >
                    Upload Image
                  </label>
                </div>
              </td>

              {/* Actions */}
              <td>
                <div style={{ display: "flex", gap: "5px" }}>
                  {editMode === vibe.id ? (
                    <>
                      <button onClick={() => handleEditVibe(vibe.id)} style={styles.button}>
                        Save
                      </button>
                      <button onClick={() => setEditMode(null)} style={{ ...styles.button, backgroundColor: "silver", color: "black" }}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        setEditMode(vibe.id);
                        setEditedVibeName(vibe.name);
                        setNewVibeCategoryName(vibe.categoryName || "");
                      }}
                      style={styles.button}
                    >
                      Edit
                    </button>
                  )}
                  <button onClick={() => handleDeleteVibe(vibe.id)} style={{ ...styles.button, backgroundColor: "red" }}>
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/** Add new vibe */}
      <div style={{ display: "flex", alignItems: "center", marginTop: 20 }}>
        <input type="text" value={newVibeName} onChange={(e) => setNewVibeName(e.target.value)} placeholder="New Vibe Name" style={styles.input} />
        <input
          type="text"
          value={newVibeCategoryName}
          onChange={(e) => setNewVibeCategoryName(e.target.value)}
          placeholder="Category Key"
          style={{ ...styles.input, marginLeft: "10px" }}
        />
        <button onClick={handleAddVibe} style={styles.button}>
          Add
        </button>
      </div>
    </div>
  );
};

const styles = {
  table: { borderCollapse: "collapse", width: "100%" },
  th: { padding: "8px", textAlign: "left", borderBottom: "1px solid #ccc", fontSize: 12 },
  td: { padding: "8px" },
  input: { padding: "10px", width: "100%", border: "1px solid #ccc", borderRadius: "4px" },
  button: { padding: "8px 12px", backgroundColor: "#478169", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", marginLeft: "5px" },
  tag: { marginRight: "5px", padding: "6px 12px", backgroundColor: "#ccc", borderRadius: "25px", display: "inline-block", fontSize: 12 },
  deleteTagButton: { marginLeft: "5px", cursor: "pointer", border: "none", background: "none" },
  addTagButton: { marginLeft: "5px", cursor: "pointer", padding: "5px", backgroundColor: "#478169", color: "white", border: "none", borderRadius: "4px" },
};

export default ProductVibes;
